import DOMPurify from "dompurify";
import React, { useMemo } from "react";

import { BACKEND_URL } from "../../lib/api";

// Allow only safe presentational style properties (alignment, text-decoration).
// This strips the hard-coded `color` / `background-color` declarations that
// Word / Quill paste-from-rich-text adds on every span, which otherwise wash
// the body text out to light grey on every blog post.
const SAFE_STYLE_PROPS = new Set([
  "text-align",
  "text-decoration",
  "font-weight",
  "font-style",
]);

const sanitiseStyle = (raw) => {
  if (!raw) return "";
  return raw
    .split(";")
    .map((d) => d.trim())
    .filter(Boolean)
    .filter((d) => {
      const key = d.split(":")[0]?.trim().toLowerCase();
      return SAFE_STYLE_PROPS.has(key);
    })
    .join("; ");
};

// 1. Rewrite stored-asset paths to absolute backend origin.
// 2. Collapse word-separator non-breaking spaces back to normal spaces so the
//    browser can wrap & justify long paragraphs (the WP/Word paste flow tends
//    to emit `&nbsp;` between every word, which breaks alignment).
const rewriteHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/(src|href)="\/api\/files\//g, `$1="${BACKEND_URL}/api/files/`)
    .replace(/(src|href)="\/uploads\//g, `$1="${BACKEND_URL}/api/uploads/`)
    // Word-separator nbsp -> regular space (keeps leading/trailing nbsp blocks
    // used for indentation by collapsing only `&nbsp;` surrounded by word chars
    // or at boundaries between visible text and other nbsps).
    .replace(/(\S)&nbsp;(?=\S)/g, "$1 ")
    .replace(/(\S)\u00a0(?=\S)/g, "$1 ");
};

const Html = ({ html, className = "" }) => {
  const clean = useMemo(() => {
    const rewritten = rewriteHtml(html || "");
    // Per-element hook: rewrite the `style` attribute to keep only safe props.
    DOMPurify.removeAllHooks();
    DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
      if (data.attrName === "style") {
        data.attrValue = sanitiseStyle(data.attrValue);
        if (!data.attrValue) data.keepAttr = false;
      }
    });
    const out = DOMPurify.sanitize(rewritten, { ADD_ATTR: ["target", "rel"] });
    DOMPurify.removeAllHooks();
    return out;
  }, [html]);

  return (
    <div
      className={`rich-content ${className}`}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

export default Html;
